import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  MatLegacyPaginator as MatPaginator,
  LegacyPageEvent as PageEvent,
} from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort, Sort } from '@angular/material/sort';

import { ContentStatus, IColumn } from '@products/interfaces/models';

import { ListTableService } from './services/list-table.service';

@Component({
  selector: 'app-list-table',
  templateUrl: './list-table.component.html',
  styleUrls: ['./list-table.component.scss'],
})
export class ListTableComponent implements AfterViewInit {
  @Input() dataSource = new MatTableDataSource<any>();
  @Input() columns: IColumn[] = [];
  @Input() hiddenColumns: string[] = [];
  @Input() pageable: boolean = true;
  @Input() numberOfItems: number = 0;
  @Input() pageSizeOptions: number[] = [30, 60, 90];
  @Input() customCellValue: Function = () => null;
  @Input() customCellTooltip: Function = () => null;
  @Input() rowIdName = 'id';
  @Input() customCellLink: Function = () => ({
    routerLink: null,
    queryParams: null,
  });
  @Input() isLoading: boolean = false;
  @Input() pageSize: number = 30;
  @Input() routeBase: string = '';
  @Input() defaultStatus: ContentStatus = ContentStatus.Published;
  @ContentChild(TemplateRef) actionsCell!: TemplateRef<any>;

  @Output() public sortChangedEvent = new EventEmitter<Sort>();
  @Output() public selectionChangedEvent = new EventEmitter<
    SelectionModel<string>
  >();

  @ViewChild(MatSort) sort = new MatSort();
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  public displayedColumns: string[] = [];
  public formGroup: UntypedFormGroup = new UntypedFormGroup({});
  public filters: any[] = [];
  selection = new SelectionModel<string>(true, []);

  constructor(
    private listTableService: ListTableService,
    private fb: UntypedFormBuilder
  ) {}

  ngAfterViewInit(): void {
    this.columns
      .filter((x) => x.filterable || x.searchable)
      .forEach((item) => {
        this.filters.push(item.field);
      });
    let group: Record<string, UntypedFormControl> = {};
    this.filters.forEach((item) => {
      const _item = this.columns.find((x) => x.field === item);
      if (_item && _item.filterOptions) {
        group[item] = new UntypedFormControl(_item.filterOptions[0]?.value);
      } else {
        group[item] = new UntypedFormControl('');
      }
    });
    group['page'] = new UntypedFormControl(0);
    group['size'] = new UntypedFormControl(this.pageSize);
    if (!group['status']) {
      group['status'] = new UntypedFormControl(this.defaultStatus);
    }
    this.formGroup = this.fb.group(group);

    this.dataSource.sort = this.sort;
    this.displayedColumns = this.columns.map((column: IColumn) => column.field);
    this.displayedColumns = this.displayedColumns.filter(
      (column) => !this.hiddenColumns.includes(column)
    );
  }

  public filterChanged(): void {
    this.listTableService.setFilter({ ...this.formGroup.value, page: 0 });
    this.paginator.pageIndex = 0;
  }

  public sortChanged(event: Sort): void {
    this.sortChangedEvent.emit(event);
  }

  public pageChanged(pageEvent: PageEvent): void {
    this.formGroup.get('page')?.setValue(pageEvent.pageIndex);
    this.formGroup.get('size')?.setValue(pageEvent.pageSize);
    this.listTableService.setFilter(this.formGroup.value);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.selectionChangedEvent.emit(this.selection);
      return;
    }

    this.selection.select(
      ...this.dataSource.data.map((row) => row[this.rowIdName])
    );
    this.selectionChangedEvent.emit(this.selection);
  }

  toggleRow(id: string) {
    this.selection.toggle(id);
    this.selectionChangedEvent.emit(this.selection);
  }

  checkCheckBox() {
    const dataIds = this.dataSource.data.map((row) => row[this.rowIdName]);
    const diff = this.selection.selected.filter(
      (sel) => !dataIds.includes(sel)
    );
    diff.forEach((el) => this.selection.deselect(el));
  }

  checkboxLabel(id?: string): string {
    if (!id) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(id) ? 'deselect' : 'select'}`;
  }

  public customTrackBy(_: number, column: IColumn): string {
    return column.field;
  }
}
